import type NumberUnit from '@exodus/currency';
import type { Selector } from 'reselect';

import type { Assets, FeesResult } from '@/types/index.js';
import { balancesLoadedSelector } from './asset-balance.js';
import getSpendableBalanceSelector from './get-spendable-balance.js';
import selectors from '../../selectors.js';
import createDeepEqualSelector from '../../utils/deep-equals.js';

type GetAvailableBalanceFn = (
  params: { assetName: string; walletAccount: string },
  fees: FeesResult,
) => NumberUnit | undefined;

const getAvailableBalanceSelector: Selector<unknown, GetAvailableBalanceFn> =
  createDeepEqualSelector(
    selectors.availableAssets.all,
    balancesLoadedSelector,
    getSpendableBalanceSelector,
    (
      assets: Assets,
      balancesLoaded,
      getSpendableBalance: ReturnType<typeof getSpendableBalanceSelector>,
    ): GetAvailableBalanceFn =>
      ({ assetName, walletAccount }, fees) => {
        const asset = assets[assetName];
        if (!asset || !balancesLoaded) {
          return;
        }

        const spendableBalance = getSpendableBalance({
          assetName,
          walletAccount,
        });

        const isFeeCurrencyDifferent = asset.feeAsset.name !== asset.name;

        const { currency } = asset;
        const fee = isFeeCurrencyDifferent
          ? currency.ZERO
          : fees?.unspendableFee || fees?.fee || currency.ZERO;
        return spendableBalance.sub(fee).clampLowerZero();
      },
  );

export default getAvailableBalanceSelector;
